import { Component, Inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { MatStepper } from '@angular/material/stepper';
import { ErrorTypes } from 'src/app/shared/enums/error-types.enum';
import { EstadosTransferenciaManual } from 'src/app/shared/enums/estados-transferencia-manual.enum';
import { Monedas } from 'src/app/shared/enums/monedas';
import { ParametrosEnum } from 'src/app/shared/enums/parameters.enum';
import { ActionsService } from 'src/app/shared/services/actions.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { ManualTransactionService } from '../../services/manual-transaction.service';
import { N58Service } from '../../services/n58.service';
import { ParameterService } from '../../services/parameter.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog-component/confirmation-dialog-component.component';

@Component({
  selector: 'app-create-transaction-dialog',
  templateUrl: './create-transaction-dialog.component.html',
  styleUrls: ['./create-transaction-dialog.component.sass'],
})
export class CreateFormDialogComponent implements OnInit, OnDestroy {
  createForm: FormGroup | undefined = undefined;

  selectedCurrenLocal: { [key: string]: string } = {
    [Monedas.CL]: 'CLP',
  };
  selectedCurren: { [key: string]: string } = {
    [Monedas.CL]: 'CLP',
    [Monedas.USDT]: 'USDT',
  };
  filtersLocal: string[] = [<string>Monedas.CL];
  filters: string[] = [<string>Monedas.CL, <string>Monedas.USDT];
  factorManual: number = 0;
  precioSpot: number = 0;
  walletValidated: boolean = false;
  walletValidating: boolean = false;
  walletBeneficiario: string | null = null;
  walletError: string | null = null;
  private lastValidatedWallet: string = '';
  private walletSub: any;
  @ViewChild('stepper') stepper: MatStepper | undefined;
  constructor(
    private actionService: ActionsService,
    private dialogRef: MatDialogRef<CreateFormDialogComponent>,
    private manualTransactionService: ManualTransactionService,
    private n58Service: N58Service,
    private spinnerService: SpinnerService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private parameterService: ParameterService
  ) {
    this.createForm =
      data.createForm ||
      this.fb.group({
        usuarioId: new FormControl({ value: data.usuarioId, disabled: true }, [
          Validators.required,
        ]),
        operatorId: new FormControl(data.operator, [Validators.required]),
        totalEnvio: new FormControl(null, [Validators.required]),
        totalDest: new FormControl({ value: null, disabled: true }, [
          Validators.required,
        ]),
        monedaLocal: new FormControl(Monedas.CL, [Validators.required]),
        monedaDestino: new FormControl(Monedas.USDT, [Validators.required]),
        tasa: new FormControl(null, [
          Validators.required,
          this.minDynamicValueValidator(
            () => +this.factorManual + +this.precioSpot
          ),
        ]),
        walletTrx: new FormControl(null, [Validators.required]),
      });
    this.createForm?.get('usuarioId')?.disabled;
    this.createForm?.get('totalDest')?.disabled;
  }
  async ngOnInit(): Promise<void> {
    await this.loadParametersData();
    this.setupWalletValidation();
  }

  ngOnDestroy(): void {
    this.walletSub?.unsubscribe();
  }

  private setupWalletValidation(): void {
    const walletCtrl = this.createForm?.get('walletTrx');
    if (!walletCtrl) return;

    this.walletSub = walletCtrl.valueChanges.pipe(
      // Reset inmediato en cada keystroke (antes del debounce)
      tap(() => {
        this.walletValidated = false;
        this.walletBeneficiario = null;
        this.walletError = null;
        this.walletValidating = false;
        this.lastValidatedWallet = '';
      }),
      debounceTime(400),
      distinctUntilChanged(),
    ).subscribe((value: string) => {
      const wallet = value?.trim() || '';

      if (!wallet || wallet.length < 10) {
        return;
      }

      this.walletValidating = true;

      this.n58Service.validateCryptoWallet(this.data.usuarioId, wallet).subscribe({
        next: (res) => {
          // Solo aplicar si la wallet no cambió mientras validaba
          if (walletCtrl.value?.trim() !== wallet) return;

          this.walletValidating = false;
          if (res.success && res.match) {
            this.walletValidated = true;
            this.walletBeneficiario = res.beneficiario?.nombre || 'Beneficiario encontrado';
            this.lastValidatedWallet = wallet;
          } else {
            this.walletValidated = false;
            this.walletError = res.message || 'No se encontró beneficiario con esa wallet para este usuario.';
          }
        },
        error: (err) => {
          if (walletCtrl.value?.trim() !== wallet) return;

          this.walletValidating = false;
          this.walletValidated = false;
          this.walletError = typeof err === 'string' ? err : 'Error al validar la wallet.';
        },
      });
    });
  }
  async loadParametersData() {
    let precioSpotParameter = await this.parameterService.getParameter(
      ParametrosEnum.PRECIO_SPOT
    );
    let factorManualParameter = await this.parameterService.getParameter(
      ParametrosEnum.FACTOR_MANUAL
    );
    this.precioSpot = precioSpotParameter.valor || 0;
    this.factorManual = factorManualParameter.valor || 0;
  }
  onSubmit() {
    if (this.createForm?.valid) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '450px',
        data: {
          formData: this.createForm.value,
          userid: this.data.usuarioId,
          nombre: this.data.nombre,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const formData = this.createForm?.value;
          this.recordTrx();
        } else {
          this.closeDialog();
        }
      });
    }
  }

  async recordTrx() {
    const ref = this.spinnerService.start();
    const transaccionDTO = {
      usuarioId: this.data.usuarioId,
      operatorId: this.data.operator,
      totalEnvio: this.createForm?.get('totalEnvio')?.value,
      totalDest: this.createForm?.get('totalDest')?.value,
      monedaLocal: this.createForm?.get('monedaLocal')?.value,
      monedaDestino: this.createForm?.get('monedaDestino')?.value,
      valorPagado: 0,
      tasa: this.createForm?.get('tasa')?.value,
      walletTrx: this.createForm?.get('walletTrx')?.value,
      estado: EstadosTransferenciaManual.ABIERTA,
    };
    try {
      const resultadoTransaccion =
        await this.manualTransactionService.createManualTransaction(
          transaccionDTO
        );
      this.spinnerService.stop(ref);
      Swal.fire(
        'Transaccion creada',
        `Se ha creado la transaccion con éxito`,
        'success'
      );
      // this.router.navigate(['/wallet']);
      this.dialogRef.close();
    } catch (error: any) {
      if (
        error.status === 400 &&
        error.error.type === ErrorTypes.ERROR_CREANDO
      ) {
        this.spinnerService.stop(ref);
        if (this.stepper) {
          this.actionService.emitirAccionTasaFiat();
          this.stepper.reset();
        }
        this.dialogRef.close();
      } else {
        Swal.fire('Error al enviar dinero', error.error.title, 'warning').then(
          () => {
            this.spinnerService.stop(ref);
            // this.router.navigate(['/wallet']);
            this.dialogRef.close();
          }
        );
      }
    }
  }

  get isSubmitDisabled(): boolean {
    return !this.createForm?.valid || !this.walletValidated || this.walletValidating;
  }

  closeDialog() {
    this.dialogRef.close();
  }
  loadDestino() {
    if (this.createForm?.value.tasa && this.createForm?.value.totalEnvio) {
      this.createForm
        .get('totalDest')
        ?.setValue(
          +this.createForm?.get('totalEnvio')?.value /
            +this.createForm?.get('tasa')?.value
        );
    }
  }
  loadDestinoOnTasaChange() {
    if (this.createForm?.value.tasa && this.createForm?.value.totalEnvio) {
      this.createForm
        .get('totalDest')
        ?.setValue(
          +this.createForm?.get('totalEnvio')?.value /
            +this.createForm?.get('tasa')?.value
        );
    }
  }

  minDynamicValueValidator(getMin: () => number): ValidatorFn {
    return (control: AbstractControl) => {
      const min = getMin();
      if (control.value !== null && control.value < min) {
        return { minValue: { requiredMin: min, actual: control.value } };
      }
      return null;
    };
  }
}
